import { loadPerformerDirectory } from '@/performers/loadDirectory';
import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Box,
  Divider,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import { usePerformersApi, useAppContext } from "@/context/AppContext";
import { apiDateToDateOnly } from "@/utils/dateUtils";
import type { PerformerList, ShowPerformer } from "api-client/models";

interface ShowPerformerManagerProps {
  showId: string;
  performers: ShowPerformer[];
  showDealType?: string | null;
  showDoorDealPercentageOwner?: string | null;
  showDoorDealPercentage?: string | null;
  onUpdate: () => void | Promise<void>;
}

const DEAL_TYPES = [
  { value: "guarantee", label: "Guarantee" },
  { value: "percentage", label: "Percentage" },
  { value: "guarantee_plus", label: "Guarantee + Percentage" },
  { value: "door_deal", label: "Door Deal" },
  { value: "comp", label: "Comp (Free)" },
];

export const ShowPerformerManager: React.FC<ShowPerformerManagerProps> = ({
  showId,
  performers,
  showDealType,
  showDoorDealPercentageOwner,
  showDoorDealPercentage,
  onUpdate,
}) => {
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [availablePerformers, setAvailablePerformers] = useState<
    PerformerList[]
  >([]);
  const [selectedPerformerId, setSelectedPerformerId] = useState("");
  const [editingPerformer, setEditingPerformer] =
    useState<ShowPerformer | null>(null);

  // Form state
  const [billingOrder, setBillingOrder] = useState<number>(1);
  const [setTime, setSetTime] = useState("");
  const [setDuration, setSetDuration] = useState<number>(45);
  const [dealType, setDealType] = useState("guarantee");
  const [guaranteeAmount, setGuaranteeAmount] = useState("");
  const [percentage, setPercentage] = useState("");
  const [isPaid, setIsPaid] = useState(false);
  const [paymentDate, setPaymentDate] = useState("");
  const [actualPayment, setActualPayment] = useState("");
  const [notes, setNotes] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const DOOR_DEAL_OWNER_LABELS: Record<string, string> = {
    house: "House",
    promoter: "Promoter",
    house_is_promoter: "House (also promoter)",
    custom: "Custom / Negotiated",
  };

  const resolvedDealType = showDealType || dealType || "guarantee";
  const resolvedDealLabel =
    DEAL_TYPES.find((option) => option.value === resolvedDealType)?.label ||
    resolvedDealType;

  const performersApi = usePerformersApi();
  const { team } = useAppContext();

  // Helper function to convert time string (HH:mm) to Date object
  const timeStringToDate = (timeStr: string): Date | null => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(":");
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    date.setSeconds(0);
    return date;
  };

  // Helper function to convert Date object to time string (HH:mm)
  const dateToTimeString = (date: Date | null): string => {
    if (!date) return "";
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  useEffect(() => {
    if (openAdd) {
      fetchAvailablePerformers();
    }
  }, [openAdd]);

  const fetchAvailablePerformers = async () => {
    try {
      const response = await loadPerformerDirectory(performersApi);
      setAvailablePerformers(response);
    } catch {
      setError("Failed to load performers");
    }
  };

  const resetForm = () => {
    setSelectedPerformerId("");
    setBillingOrder(1);
    setSetTime("");
    setSetDuration(45);
    setDealType(showDealType || "guarantee");
    setGuaranteeAmount("");
    setPercentage("");
    setIsPaid(false);
    setPaymentDate("");
    setActualPayment("");
    setNotes("");
    setEditingPerformer(null);
    setFieldErrors({});
  };

  useEffect(() => {
    if (openAdd && showDealType) {
      setDealType(showDealType);
    }
  }, [openAdd, showDealType]);

  const handleAddPerformer = async () => {
    if (!selectedPerformerId || loading) return;

    setLoading(true);
    setError(null);
    setFieldErrors({});

    try {
      // Send the ShowPerformer data directly (not nested under 'show')
      // The OpenAPI schema is wrong - the backend expects ShowPerformerSerializer fields
      const response = await fetch(`/api/shows/${showId}/add_performer/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Team-ID": String(team?.id ?? ''),
          "X-CSRFToken":
            document.cookie
              .split("; ")
              .find((row) => row.startsWith("csrftoken="))
              ?.split("=")[1] || "",
        },
        credentials: "include",
        body: JSON.stringify({
          performer_id: selectedPerformerId,
          billing_order: billingOrder,
          set_time: setTime || null, // null instead of empty string for time fields
          set_duration: setDuration || null,
          deal_type: showDealType || dealType,
          guarantee_amount: guaranteeAmount || "",
          percentage: percentage || "",
          is_paid: isPaid,
          payment_date: paymentDate || null, // null instead of empty string for date fields
          actual_payment: actualPayment || "",
          notes: notes || "",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Parse field-level errors from Django REST Framework
        if (typeof errorData === "object" && !errorData.detail) {
          const errors: Record<string, string> = {};
          Object.keys(errorData).forEach((key) => {
            const errorValue = errorData[key];
            if (Array.isArray(errorValue)) {
              errors[key] = errorValue[0];
            } else if (typeof errorValue === "string") {
              errors[key] = errorValue;
            }
          });
          setFieldErrors(errors);
          setError("Please check the form for errors.");
        } else {
          throw new Error(errorData.detail || JSON.stringify(errorData));
        }
        return;
      }

      try {
        await onUpdate();
      } catch {
        throw new Error("Performer added, but refresh failed. Reload to verify before adding again.");
      }
      setOpenAdd(false);
      resetForm();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to add performer";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (showPerformer: ShowPerformer) => {
    setEditingPerformer(showPerformer);
    setBillingOrder(showPerformer.billingOrder || 1);
    setSetTime(showPerformer.setTime || "");
    setSetDuration(showPerformer.setDuration || 45);
    setDealType(showPerformer.dealType || "guarantee");
    setGuaranteeAmount(showPerformer.guaranteeAmount || "");
    setPercentage(showPerformer.percentage || "");
    setIsPaid(showPerformer.isPaid || false);
    setPaymentDate(
      apiDateToDateOnly(showPerformer.paymentDate),
    );
    setActualPayment(showPerformer.actualPayment || "");
    setNotes(showPerformer.notes || "");
    setOpenEdit(true);
  };

  const handleUpdatePerformer = async () => {
    if (!editingPerformer || loading) return;

    setLoading(true);
    setError(null);
    setFieldErrors({});

    try {
      // Send the ShowPerformer data directly with performer_id to identify the record
      const response = await fetch(`/api/shows/${showId}/update_performer/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-Team-ID": String(team?.id ?? ''),
          "X-CSRFToken":
            document.cookie
              .split("; ")
              .find((row) => row.startsWith("csrftoken="))
              ?.split("=")[1] || "",
        },
        credentials: "include",
        body: JSON.stringify({
          performer_id: editingPerformer.performer.id,
          billing_order: billingOrder,
          set_time: setTime || null, // null instead of empty string for time fields
          set_duration: setDuration || null,
          deal_type: showDealType || dealType,
          guarantee_amount:
            guaranteeAmount || editingPerformer.guaranteeAmount || "",
          percentage: percentage || editingPerformer.percentage || "",
          is_paid: isPaid,
          payment_date: paymentDate || null, // null instead of empty string for date fields
          actual_payment: actualPayment || "",
          notes: notes || "",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Parse field-level errors from Django REST Framework
        if (typeof errorData === "object" && !errorData.detail) {
          const errors: Record<string, string> = {};
          Object.keys(errorData).forEach((key) => {
            const errorValue = errorData[key];
            if (Array.isArray(errorValue)) {
              errors[key] = errorValue[0];
            } else if (typeof errorValue === "string") {
              errors[key] = errorValue;
            }
          });
          setFieldErrors(errors);
          setError("Please check the form for errors.");
        } else {
          throw new Error(errorData.detail || JSON.stringify(errorData));
        }
        return;
      }

      try {
        await onUpdate();
      } catch {
        throw new Error("Performer updated, but refresh failed. Reload to verify current values.");
      }
      setOpenEdit(false);
      resetForm();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update performer";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePerformer = async (showPerformer: ShowPerformer) => {
    if (!confirm("Remove this performer from the show?")) return;

    try {
      const response = await fetch(
        `/api/shows/${showId}/remove_performer/?performer_id=${showPerformer.performer.id}`,
        {
          method: "DELETE",
          headers: {
            "X-CSRFToken":
              document.cookie
                .split("; ")
                .find((row) => row.startsWith("csrftoken="))
                ?.split("=")[1] || "",
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      try {
        await onUpdate();
      } catch {
        throw new Error("Performer removed, but refresh failed. Reload to verify current performers.");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to remove performer";
      alert(errorMessage);
    }
  };

  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num);
  };

  const renderPerformerForm = () => (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!editingPerformer && (
        <TextField
          select
          label="Select Performer"
          value={selectedPerformerId}
          onChange={(e) => {
            setSelectedPerformerId(e.target.value);
            setFieldErrors({ ...fieldErrors, performer_id: "" });
          }}
          fullWidth
          required
          error={!!fieldErrors.performer_id}
          helperText={fieldErrors.performer_id}
        >
          {availablePerformers.map((performer) => (
            <MenuItem key={performer.id} value={performer.id}>
              {performer.performerName}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Billing Order"
          type="number"
          value={billingOrder}
          onChange={(e) => {
            setBillingOrder(Number(e.target.value));
            setFieldErrors({ ...fieldErrors, billing_order: "" });
          }}
          fullWidth
          helperText={
            fieldErrors.billing_order || "1 = Headliner, 2 = Support, etc."
          }
          inputProps={{ min: 1 }}
          error={!!fieldErrors.billing_order}
        />
        <TextField
          label="Set Duration (minutes)"
          type="number"
          value={setDuration}
          onChange={(e) => {
            setSetDuration(Number(e.target.value));
            setFieldErrors({ ...fieldErrors, set_duration: "" });
          }}
          fullWidth
          inputProps={{ min: 0 }}
          error={!!fieldErrors.set_duration}
          helperText={fieldErrors.set_duration}
        />
      </Stack>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <TimePicker
          label="Set Time"
          value={timeStringToDate(setTime)}
          onChange={(newValue) => {
            setSetTime(dateToTimeString(newValue));
            setFieldErrors({ ...fieldErrors, set_time: "" });
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              error: !!fieldErrors.set_time,
              helperText: fieldErrors.set_time,
            },
          }}
        />
      </LocalizationProvider>

      <Divider />

      <Typography variant="subtitle2">Payment Details</Typography>

      <Alert severity="info" variant="outlined">
        Deal type comes from show settings: <strong>{resolvedDealLabel}</strong>
      </Alert>

      {resolvedDealType === "door_deal" && (
        <Alert severity="info" variant="outlined">
          <Stack spacing={0.5}>
            <Typography variant="body2">
              Percentage owner: <strong>{DOOR_DEAL_OWNER_LABELS[showDoorDealPercentageOwner || ""] || "Not set"}</strong>
            </Typography>
            <Typography variant="body2">
              Percentage: <strong>{showDoorDealPercentage || "Not set"}%</strong>
            </Typography>
            {showDoorDealPercentage && (
              <Typography variant="caption" color="text.secondary">
                Remaining split for other performers: {Math.max(0, 100 - Number(showDoorDealPercentage || 0)).toFixed(2)}%
              </Typography>
            )}
          </Stack>
        </Alert>
      )}

      <Divider />

      <FormControlLabel
        control={
          <Checkbox
            checked={isPaid}
            onChange={(e) => setIsPaid(e.target.checked)}
          />
        }
        label="Mark as paid"
      />

      {isPaid && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Payment Date"
            type="date"
            value={paymentDate}
            onChange={(e) => {
              setPaymentDate(e.target.value);
              setFieldErrors({ ...fieldErrors, payment_date: "" });
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            error={!!fieldErrors.payment_date}
            helperText={fieldErrors.payment_date}
          />
          <TextField
            label="Actual Payment"
            type="number"
            value={actualPayment}
            onChange={(e) => {
              setActualPayment(e.target.value);
              setFieldErrors({ ...fieldErrors, actual_payment: "" });
            }}
            fullWidth
            inputProps={{ step: "0.01", min: "0" }}
            error={!!fieldErrors.actual_payment}
            helperText={fieldErrors.actual_payment}
          />
        </Stack>
      )}

      <TextField
        label="Notes"
        value={notes}
        onChange={(e) => {
          setNotes(e.target.value);
          setFieldErrors({ ...fieldErrors, notes: "" });
        }}
        multiline
        rows={3}
        fullWidth
        error={!!fieldErrors.notes}
        helperText={fieldErrors.notes}
      />
    </Stack>
  );

  return (
    <Card>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">Performers</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setOpenAdd(true)}
          >
            Add Performer
          </Button>
        </Stack>

        {performers.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No performers assigned yet
          </Typography>
        ) : (
          <Stack spacing={1}>
            {performers
              .sort((a, b) => (a.billingOrder || 999) - (b.billingOrder || 999))
              .map((showPerformer) => (
                <Card key={showPerformer.id} variant="outlined">
                  <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          {showPerformer.billingOrder && (
                            <Chip
                              label={`#${showPerformer.billingOrder}`}
                              size="small"
                              color="primary"
                            />
                          )}
                          <Typography variant="body1" fontWeight="medium">
                            {showPerformer.performer.performerName}
                          </Typography>
                          {showPerformer.dealType && (
                            <Chip
                              label={
                                DEAL_TYPES.find(
                                  (d) => d.value === showPerformer.dealType,
                                )?.label || showPerformer.dealType
                              }
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {showPerformer.isPaid && (
                            <Chip label="Paid" size="small" color="success" />
                          )}
                        </Stack>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(showPerformer)}
                            aria-label={`Edit performer ${showPerformer.performer.performerName}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={`Remove performer ${showPerformer.performer.performerName}`}
                            onClick={() => handleRemovePerformer(showPerformer)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                        {showPerformer.setTime && (
                          <Typography variant="body2" color="text.secondary">
                            Set: {showPerformer.setTime}
                          </Typography>
                        )}
                        {showPerformer.setDuration && (
                          <Typography variant="body2" color="text.secondary">
                            Duration: {showPerformer.setDuration} min
                          </Typography>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          <MoneyIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {formatCurrency(showPerformer.calculatedPayment)}
                          </Typography>
                        </Stack>
                      </Stack>

                      {showPerformer.notes && (
                        <Typography variant="body2" color="text.secondary">
                          {showPerformer.notes}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
          </Stack>
        )}
      </CardContent>

      {/* Add Performer Dialog */}
      <Dialog
        open={openAdd}
        onClose={loading ? undefined : () => {
          setOpenAdd(false);
          resetForm();
        }}
        disableEscapeKeyDown={loading}
        closeAfterTransition={false}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Performer to Show</DialogTitle>
        <DialogContent>{renderPerformerForm()}</DialogContent>
        <DialogActions>
          <Button
            disabled={loading}
            autoFocus
            onClick={() => {
              setOpenAdd(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddPerformer}
            variant="contained"
            disabled={!selectedPerformerId || loading}
          >
            {loading ? "Adding..." : "Add Performer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Performer Dialog */}
      <Dialog
        open={openEdit}
        onClose={loading ? undefined : () => {
          setOpenEdit(false);
          resetForm();
        }}
        disableEscapeKeyDown={loading}
        closeAfterTransition={false}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Performer Details</DialogTitle>
        <DialogContent>
          {editingPerformer && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium">
                {editingPerformer.performer.performerName}
              </Typography>
            </Box>
          )}
          {renderPerformerForm()}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={loading}
            autoFocus
            onClick={() => {
              setOpenEdit(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdatePerformer}
            variant="contained"
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Performer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
